import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminShopProducts.css";

/* webpack require.context — same pattern as Shop.jsx */
let SHOP_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/shop",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  SHOP_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* running outside CRA — images will fall back to API URL */
}

function shopImg(filename) {
  if (!filename) return null;
  return SHOP_IMAGES[filename] ?? `${API_BASE}/uploads/shop/${filename}`;
}

const fmt = (n) =>
  "R " +
  Number(n ?? 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
}

const EMPTY_FORM = {
  name: "",
  slug: "",
  price: "",
  sale_price: "",
  discount_percent: "",
  sku: "",
  quantity: "",
  low_stock_alert: "5",
  car_make: "",
  car_model: "",
  car_year_from: "",
  car_year_to: "",
  condition_type: "new",
  part_number: "",
  oem_number: "",
  description: "",
  category_id: "",
  is_featured: 0,
  active: 1,
  image1: null,
  image2: null,
  image3: null,
};

/* ─────────────────────────────────
   Dot-menu (3 vertical dots)
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="asp-dot-menu" ref={ref}>
      <button
        className="asp-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="asp-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="asp-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="asp-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="asp-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="asp-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Image preview strip
   ───────────────────────────────── */
function ImageStrip({ product }) {
  const imgs = [product.image1, product.image2, product.image3].filter(Boolean);
  if (!imgs.length) return <span className="asp-no-img">—</span>;
  return (
    <div className="asp-img-strip">
      {imgs.map((f, i) => (
        <img key={i} src={shopImg(f)} alt="" className="asp-img-thumb" />
      ))}
    </div>
  );
}

/* ─────────────────────────────────
   Badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  // Coerce to number to handle both 1/"1"/true
  const isActive = Number(active) === 1;
  return (
    <span className={`asp-badge ${isActive ? "asp-badge--active" : "asp-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ─────────────────────────────────
   Stock badge
   ───────────────────────────────── */
function StockBadge({ qty, low }) {
  if (qty === 0)      return <span className="asp-stock asp-stock--out">Out of stock</span>;
  if (qty <= (low || 5)) return <span className="asp-stock asp-stock--low">Low ({qty})</span>;
  return                    <span className="asp-stock asp-stock--in">In stock ({qty})</span>;
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ product, categories, onClose }) {
  if (!product) return null;
  const cat = categories.find((c) => c.id === product.category_id);
  const imgs = [product.image1, product.image2, product.image3].filter(Boolean);

  return (
    <div className="asp-modal-overlay" onClick={onClose}>
      <div className="asp-modal asp-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="asp-modal__header">
          <div className="asp-modal__title-row">
            <span className="asp-modal__tag">VIEW</span>
            <h2 className="asp-modal__title">{product.name}</h2>
          </div>
          <button className="asp-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asp-modal__body asp-modal__body--view">
          {/* Images */}
          {imgs.length > 0 && (
            <div className="asp-view-images">
              <img src={shopImg(imgs[0])} alt="" className="asp-view-images__main" />
              {imgs.slice(1).map((f, i) => (
                <img key={i} src={shopImg(f)} alt="" className="asp-view-images__thumb" />
              ))}
            </div>
          )}

          {/* Details grid */}
          <div className="asp-view-grid">
            {[
              ["Name",          product.name],
              ["Slug",          product.slug],
              ["SKU",           product.sku || "—"],
              ["Part #",        product.part_number || "—"],
              ["OEM #",         product.oem_number || "—"],
              ["Category",      cat?.name || product.category_name || "—"],
              ["Price",         fmt(product.price)],
              ["Sale Price",    product.sale_price ? fmt(product.sale_price) : "—"],
              ["Discount",      product.discount_percent ? `${product.discount_percent}%` : "—"],
              ["Stock Qty",     product.quantity],
              ["Low Stock At",  product.low_stock_alert || 5],
              ["Condition",     product.condition_type || "—"],
              ["Car Make",      product.car_make || "—"],
              ["Car Model",     product.car_model || "—"],
              ["Year From",     product.car_year_from || "—"],
              ["Year To",       product.car_year_to || "—"],
              ["Featured",      product.is_featured ? "Yes" : "No"],
              ["Status",        Number(product.active) === 1 ? "Active" : "Inactive"],
            ].map(([label, val]) => (
              <div key={label} className="asp-view-row">
                <span className="asp-view-row__label">{label}</span>
                <span className="asp-view-row__val">{val}</span>
              </div>
            ))}
          </div>

          {/* Description */}
          {product.description && (
            <div className="asp-view-desc">
              <p className="asp-view-desc__label">Description</p>
              <p className="asp-view-desc__text">{stripHtml(product.description)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, product, categories, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [previews, setPreviews] = useState({ image1: null, image2: null, image3: null });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* Populate form when editing */
  useEffect(() => {
    if (mode === "edit" && product) {
      setForm({
        name:             product.name || "",
        slug:             product.slug || "",
        price:            product.price ?? "",
        sale_price:       product.sale_price ?? "",
        discount_percent: product.discount_percent ?? "",
        sku:              product.sku || "",
        quantity:         product.quantity ?? "",
        low_stock_alert:  product.low_stock_alert ?? "5",
        car_make:         product.car_make || "",
        car_model:        product.car_model || "",
        car_year_from:    product.car_year_from || "",
        car_year_to:      product.car_year_to || "",
        condition_type:   product.condition_type || "new",
        part_number:      product.part_number || "",
        oem_number:       product.oem_number || "",
        description:      product.description || "",
        // FIX: coerce to string so <select> value comparison works correctly
        category_id:      product.category_id != null ? String(product.category_id) : "",
        is_featured:      product.is_featured ?? 0,
        // FIX: coerce to number so checkbox checked state is reliable
        active:           Number(product.active) === 1 ? 1 : 0,
        image1: null,
        image2: null,
        image3: null,
      });
      setPreviews({
        image1: product.image1 ? shopImg(product.image1) : null,
        image2: product.image2 ? shopImg(product.image2) : null,
        image3: product.image3 ? shopImg(product.image3) : null,
      });
    }
  }, [mode, product]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* Auto-slug from name (create only) */
  const handleNameChange = (val) => {
    set("name", val);
    if (mode === "create") {
      set("slug", val.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""));
    }
  };

  const handleFile = (key, file) => {
    if (!file) return;
    set(key, file);
    const reader = new FileReader();
    reader.onload = (e) =>
      setPreviews((p) => ({ ...p, [key]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.name.trim()) return setError("Product name is required.");
    if (!form.price)        return setError("Price is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") fd.append(k, v);
      });

      if (mode === "create") {
        await apiFetch("/api/admin/shop/products", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/shop/products/${product.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asp-modal-overlay" onClick={onClose}>
      <div className="asp-modal asp-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="asp-modal__header">
          <div className="asp-modal__title-row">
            <span className="asp-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="asp-modal__title">
              {mode === "create" ? "Add Product" : "Edit Product"}
            </h2>
          </div>
          <button className="asp-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asp-modal__body asp-modal__body--form">
          {error && <div className="asp-modal__error">⚠ {error}</div>}

          {/* ── Section: Basic Info ── */}
          <div className="asp-form-section">
            <h3 className="asp-form-section__title">Basic Information</h3>
            <div className="asp-form-grid">
              <div className="asp-form-field asp-form-field--full">
                <label>Product Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Front Brake Pads Set"
                />
              </div>
              <div className="asp-form-field">
                <label>Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder="auto-generated"
                />
              </div>
              <div className="asp-form-field">
                <label>Category</label>
                {/* FIX: option values are strings; form.category_id is now also a string */}
                <select
                  value={form.category_id}
                  onChange={(e) => set("category_id", e.target.value)}
                >
                  <option value="">— Select category —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="asp-form-field">
                <label>SKU</label>
                <input
                  type="text"
                  value={form.sku}
                  onChange={(e) => set("sku", e.target.value)}
                  placeholder="e.g. SKU-001"
                />
              </div>
              <div className="asp-form-field">
                <label>Part Number</label>
                <input
                  type="text"
                  value={form.part_number}
                  onChange={(e) => set("part_number", e.target.value)}
                />
              </div>
              <div className="asp-form-field">
                <label>OEM Number</label>
                <input
                  type="text"
                  value={form.oem_number}
                  onChange={(e) => set("oem_number", e.target.value)}
                />
              </div>
              <div className="asp-form-field">
                <label>Condition</label>
                <select
                  value={form.condition_type}
                  onChange={(e) => set("condition_type", e.target.value)}
                >
                  <option value="new">New</option>
                  <option value="used">Used</option>
                  <option value="refurbished">Refurbished</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Section: Pricing & Stock ── */}
          <div className="asp-form-section">
            <h3 className="asp-form-section__title">Pricing &amp; Stock</h3>
            <div className="asp-form-grid">
              <div className="asp-form-field">
                <label>Price (R) <span className="req">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="asp-form-field">
                <label>Sale Price (R)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.sale_price}
                  onChange={(e) => set("sale_price", e.target.value)}
                  placeholder="optional"
                />
              </div>
              <div className="asp-form-field">
                <label>Discount %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.discount_percent}
                  onChange={(e) => set("discount_percent", e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="asp-form-field">
                <label>Stock Qty</label>
                <input
                  type="number"
                  min="0"
                  value={form.quantity}
                  onChange={(e) => set("quantity", e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="asp-form-field">
                <label>Low Stock Alert</label>
                <input
                  type="number"
                  min="0"
                  value={form.low_stock_alert}
                  onChange={(e) => set("low_stock_alert", e.target.value)}
                  placeholder="5"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Vehicle Fit ── */}
          <div className="asp-form-section">
            <h3 className="asp-form-section__title">Vehicle Fitment</h3>
            <div className="asp-form-grid">
              <div className="asp-form-field">
                <label>Car Make</label>
                <input
                  type="text"
                  value={form.car_make}
                  onChange={(e) => set("car_make", e.target.value)}
                  placeholder="e.g. Toyota"
                />
              </div>
              <div className="asp-form-field">
                <label>Car Model</label>
                <input
                  type="text"
                  value={form.car_model}
                  onChange={(e) => set("car_model", e.target.value)}
                  placeholder="e.g. Hilux"
                />
              </div>
              <div className="asp-form-field">
                <label>Year From</label>
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  value={form.car_year_from}
                  onChange={(e) => set("car_year_from", e.target.value)}
                  placeholder="e.g. 2015"
                />
              </div>
              <div className="asp-form-field">
                <label>Year To</label>
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  value={form.car_year_to}
                  onChange={(e) => set("car_year_to", e.target.value)}
                  placeholder="e.g. 2023"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Images ── */}
          <div className="asp-form-section">
            <h3 className="asp-form-section__title">Product Images</h3>
            <div className="asp-image-upload-row">
              {["image1", "image2", "image3"].map((key, idx) => (
                <label key={key} className="asp-image-upload">
                  {previews[key] ? (
                    <img src={previews[key]} alt="" className="asp-image-upload__preview" />
                  ) : (
                    <div className="asp-image-upload__placeholder">
                      <span>📷</span>
                      <span>Image {idx + 1}</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleFile(key, e.target.files[0])}
                  />
                  <span className="asp-image-upload__label">
                    {idx === 0 ? "Main" : `Extra ${idx}`}
                  </span>
                </label>
              ))}
            </div>
            <p className="asp-form-hint">
              Images are saved to <code>src/assets/uploads/shop/</code>. Leave blank to keep existing.
            </p>
          </div>

          {/* ── Section: Options ── */}
          <div className="asp-form-section">
            <h3 className="asp-form-section__title">Options</h3>
            <div className="asp-form-grid asp-form-grid--checks">
              <label className="asp-check">
                <input
                  type="checkbox"
                  checked={!!form.is_featured}
                  onChange={(e) => set("is_featured", e.target.checked ? 1 : 0)}
                />
                <span>Featured product</span>
              </label>
              <label className="asp-check">
                {/* FIX: compare as number so "1" from DB also evaluates as checked */}
                <input
                  type="checkbox"
                  checked={Number(form.active) === 1}
                  onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                />
                <span>Active (visible in shop)</span>
              </label>
            </div>
          </div>

          {/* ── Section: Description ── */}
          <div className="asp-form-section">
            <h3 className="asp-form-section__title">Description</h3>
            <textarea
              className="asp-form-textarea"
              rows={5}
              value={stripHtml(form.description)}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Product description…"
            />
          </div>
        </div>

        <div className="asp-modal__footer">
          <button className="asp-btn asp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="asp-btn asp-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Product" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ product, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/shop/products/${product.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="asp-modal-overlay" onClick={onClose}>
      <div className="asp-modal asp-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="asp-delete__icon">🗑</div>
        <h2 className="asp-delete__title">Delete Product?</h2>
        <p className="asp-delete__body">
          You are about to permanently delete{" "}
          <strong>{product?.name}</strong>. This action cannot be undone.
        </p>
        {error && <div className="asp-modal__error">⚠ {error}</div>}
        <div className="asp-delete__actions">
          <button className="asp-btn asp-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="asp-btn asp-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminShopProducts() {
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,    setSearch]    = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [sort,      setSort]      = useState("newest");
  const [page,      setPage]      = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null); // null | "view" | "create" | "edit" | "delete"
  const [target, setTarget] = useState(null); // selected product

  /* ── Load products ──
     FIX: use /api/admin/shop/products so inactive products are included
     and the full product record (including active flag) is returned.
  ── */
  const loadProducts = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)    q.set("search",   search);
    if (catFilter) q.set("category", catFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/shop/products?${q}`)
      .then((data) => {
        setProducts(data.products   || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, catFilter, sort, page]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  /* ── Load categories once ── */
  useEffect(() => {
    apiFetch("/api/admin/shop/categories")
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, []);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, product = null) => { setModal(type); setTarget(product); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadProducts(); };

  return (
    <AdminLayout pageTag="SHOP" pageTitle="Products">

      {error && (
        <div className="asp-error-bar">⚠ {error}</div>
      )}

      {/* ── Toolbar ── */}
      <div className="asp-toolbar">
        <div className="asp-toolbar__left">
          <div className="asp-search-wrap">
            <span className="asp-search-wrap__icon">🔍</span>
            <input
              className="asp-search"
              type="text"
              placeholder="Search products, SKU, part #…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button className="asp-search-wrap__clear" onClick={() => { setSearch(""); setPage(1); }}>
                ✕
              </button>
            )}
          </div>

          <select
            className="asp-filter-select"
            value={catFilter}
            onChange={(e) => { setCatFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>

          <select
            className="asp-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
            <option value="name_asc">Name A–Z</option>
          </select>
        </div>

        <div className="asp-toolbar__right">
          <span className="asp-toolbar__count">
            {loading ? "…" : `${pagination.total} product${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="asp-btn-primary" onClick={() => openModal("create")}>
            + Add Product
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="asp-table-skeleton" />
      ) : products.length === 0 ? (
        <div className="asp-empty">
          <span>📦</span>
          <p>No products found.</p>
          {(search || catFilter) && (
            <button
              className="asp-btn asp-btn--cancel"
              onClick={() => { setSearch(""); setCatFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="asp-table-wrap">
          <table className="asp-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>SKU / Part #</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td><ImageStrip product={p} /></td>
                  <td>
                    <div className="asp-name-cell">
                      <span className="asp-name-cell__name">{p.name}</span>
                      {p.car_make && (
                        <span className="asp-name-cell__fit">
                          {p.car_make} {p.car_model}
                          {p.car_year_from && ` (${p.car_year_from}${p.car_year_to ? `–${p.car_year_to}` : "+"})`}
                        </span>
                      )}
                      {p.is_featured === 1 && (
                        <span className="asp-featured-tag">⭐ Featured</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="asp-sku-cell">
                      {p.sku && <span>{p.sku}</span>}
                      {p.part_number && <span className="asp-sku-cell__part">#{p.part_number}</span>}
                      {!p.sku && !p.part_number && <span className="asp-muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <span className="asp-cat-tag">{p.category_name || "—"}</span>
                  </td>
                  <td>
                    <div className="asp-price-cell">
                      {p.sale_price ? (
                        <>
                          <span className="asp-price-cell__sale">{fmt(p.sale_price)}</span>
                          <span className="asp-price-cell__old">{fmt(p.price)}</span>
                        </>
                      ) : (
                        <span>{fmt(p.price)}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <StockBadge qty={p.quantity} low={p.low_stock_alert} />
                  </td>
                  <td>
                    {/* FIX: pass raw value; StatusBadge handles coercion internally */}
                    <StatusBadge active={p.active} />
                  </td>
                  <td>
                    <DotMenu
                      onView={()  => openModal("view",   p)}
                      onEdit={()  => openModal("edit",   p)}
                      onDelete={() => openModal("delete", p)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="asp-pagination">
          <button
            className="asp-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`asp-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="asp-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal   product={target} categories={categories} onClose={closeModal} />}
      {modal === "create" && <FormModal   mode="create"    categories={categories} onClose={closeModal} onSaved={afterSave} />}
      {modal === "edit"   && <FormModal   mode="edit"      product={target} categories={categories} onClose={closeModal} onSaved={afterSave} />}
      {modal === "delete" && <DeleteModal product={target} onClose={closeModal} onDeleted={afterSave} />}

    </AdminLayout>
  );
}